import { LinearScale, LogarithmicScale } from 'chart.js';
import { merge } from 'chart.js/helpers';
import { interpolateBlues, interpolateBrBG, interpolateBuGn, interpolateBuPu, interpolateCividis, interpolateCool, interpolateCubehelixDefault, interpolateGnBu, interpolateGreens, interpolateGreys, interpolateInferno, interpolateMagma, interpolateOrRd, interpolateOranges, interpolatePRGn, interpolatePiYG, interpolatePlasma, interpolatePuBu, interpolatePuBuGn, interpolatePuOr, interpolatePuRd, interpolatePurples, interpolateRainbow, interpolateRdBu, interpolateRdGy, interpolateRdPu, interpolateRdYlBu, interpolateRdYlGn, interpolateReds, interpolateSinebow, interpolateSpectral, interpolateTurbo, interpolateViridis, interpolateWarm, interpolateYlGn, interpolateYlGnBu, interpolateYlOrBr, interpolateYlOrRd, } from 'd3-scale-chromatic';
import { baseDefaults, LegendScale, LogarithmicLegendScale } from './LegendScale';
const lookup = {
    interpolateBlues,
    interpolateBrBG,
    interpolateBuGn,
    interpolateBuPu,
    interpolateCividis,
    interpolateCool,
    interpolateCubehelixDefault,
    interpolateGnBu,
    interpolateGreens,
    interpolateGreys,
    interpolateInferno,
    interpolateMagma,
    interpolateOrRd,
    interpolateOranges,
    interpolatePRGn,
    interpolatePiYG,
    interpolatePlasma,
    interpolatePuBu,
    interpolatePuBuGn,
    interpolatePuOr,
    interpolatePuRd,
    interpolatePurples,
    interpolateRainbow,
    interpolateRdBu,
    interpolateRdGy,
    interpolateRdPu,
    interpolateRdYlBu,
    interpolateRdYlGn,
    interpolateReds,
    interpolateSinebow,
    interpolateSpectral,
    interpolateTurbo,
    interpolateViridis,
    interpolateWarm,
    interpolateYlGn,
    interpolateYlGnBu,
    interpolateYlOrBr,
    interpolateYlOrRd,
};
Object.keys(lookup).forEach((key) => {
    lookup[`${key.charAt(11).toLowerCase()}${key.slice(12)}`] = lookup[key];
    lookup[key.slice(11)] = lookup[key];
});
function quantize(v, steps) {
    const perStep = 1 / steps;
    if (v <= perStep) {
        return 0;
    }
    if (v >= 1 - perStep) {
        return 1;
    }
    for (let acc = 0; acc < 1; acc += perStep) {
        if (v < acc) {
            return acc - perStep / 2;
        }
    }
    return v;
}
const colorScaleDefaults = {
    interpolate: 'blues',
    missing: 'transparent',
    quantize: 0,
};
export class ColorScale extends LegendScale {
    get interpolate() {
        const o = this.options;
        if (!o) {
            return (v) => `rgb(${v},${v},${v})`;
        }
        if (typeof o.interpolate === 'function') {
            return o.interpolate;
        }
        return lookup[o.interpolate] || lookup.blues;
    }
    getColorForValue(value) {
        const v = this._getNormalizedValue(value);
        if (v == null || Number.isNaN(v)) {
            return this.options.missing;
        }
        return this.getColor(v);
    }
    getColor(normalized) {
        let v = normalized;
        if (this.options.quantize > 0) {
            v = quantize(v, this.options.quantize);
        }
        return this.interpolate(v);
    }
    _drawIndicator() {
        const { indicatorWidth: indicatorSize } = this.options.legend;
        const reverse = this._reversePixels;
        if (this.isHorizontal()) {
            const w = this.width;
            if (this.options.quantize > 0) {
                const stepWidth = w / this.options.quantize;
                const offset = !reverse ? (i) => i : (i) => w - stepWidth - i;
                for (let i = 0; i < w; i += stepWidth) {
                    const v = (i + stepWidth / 2) / w;
                    this.ctx.fillStyle = this.getColor(v);
                    this.ctx.fillRect(offset(i), 0, stepWidth, indicatorSize);
                }
            }
            else {
                const offset = !reverse ? (i) => i : (i) => w - 1 - i;
                for (let i = 0; i < w; i += 1) {
                    this.ctx.fillStyle = this.getColor((i + 0.5) / w);
                    this.ctx.fillRect(offset(i), 0, 1, indicatorSize);
                }
            }
        }
        else {
            const h = this.height;
            if (this.options.quantize > 0) {
                const stepWidth = h / this.options.quantize;
                const offset = !reverse ? (i) => i : (i) => h - stepWidth - i;
                for (let i = 0; i < h; i += stepWidth) {
                    const v = (i + stepWidth / 2) / h;
                    this.ctx.fillStyle = this.getColor(v);
                    this.ctx.fillRect(0, offset(i), indicatorSize, stepWidth);
                }
            }
            else {
                const offset = !reverse ? (i) => i : (i) => h - 1 - i;
                for (let i = 0; i < h; i += 1) {
                    this.ctx.fillStyle = this.getColor((i + 0.5) / h);
                    this.ctx.fillRect(0, offset(i), indicatorSize, 1);
                }
            }
        }
    }
}
ColorScale.id = 'color';
ColorScale.defaults = merge({}, [LinearScale.defaults, baseDefaults, colorScaleDefaults]);
ColorScale.descriptors = {
    _scriptable: (name) => name !== 'interpolate',
    _indexable: false,
};
export class ColorLogarithmicScale extends LogarithmicLegendScale {
    constructor() {
        super(...arguments);
        this.interpolate = (v) => `rgb(${v},${v},${v})`;
    }
    init(options) {
        super.init(options);
        if (typeof options.interpolate === 'function') {
            this.interpolate = options.interpolate;
        }
        else {
            this.interpolate = lookup[options.interpolate] || lookup.blues;
        }
    }
    getColorForValue(value) {
        return ColorScale.prototype.getColorForValue.call(this, value);
    }
    getColor(normalized) {
        let v = normalized;
        if (this.options.quantize > 0) {
            v = quantize(v, this.options.quantize);
        }
        return this.interpolate(v);
    }
    _drawIndicator() {
        return ColorScale.prototype._drawIndicator.call(this);
    }
}
ColorLogarithmicScale.id = 'colorLogarithmic';
ColorLogarithmicScale.defaults = merge({}, [
    LogarithmicScale.defaults,
    baseDefaults,
    colorScaleDefaults,
]);
ColorLogarithmicScale.descriptors = {
    _scriptable: (name) => name !== 'interpolate',
    _indexable: false,
};
//# sourceMappingURL=ColorScale.js.map